import React, { useEffect, useState, useCallback, memo } from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControlLabel,
    Checkbox,
    Button,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    InputAdornment,
    CircularProgress,
    Collapse,
    Paper,
} from '@mui/material';
import {
    FaTimes,
    FaSave,
} from 'react-icons/fa';
import AllergenSelect from '../Menu/Allergens';
import SectionTitle from '../Common/Title';
import NumericField from '../Common/NumericField';

export const INGREDIENT_TYPES = {
    BASE: 'base',
    STANDARD: 'standard'
};

export interface Ingredient {
    id?: string;
    name: string;
    type: string;
    price?: number | null;           // Prix en supplément standard
    discountPrice?: number | null;   // Prix réduit en supplément standard
    baseChangePrice?: number | null; // Prix pour changer de base
    baseDoublePrice?: number | null; // Prix pour doubler l'ingrédient de base
    minQuantity?: number | null;
    maxQuantity?: number | null;
    isOutOfStock: boolean;
    allergens: string[];
    canBeExtra?: boolean;
}

interface IngredientFormProps {
    ingredient?: Ingredient;
    onSave: (ingredient: Ingredient) => Promise<void>;
    onClose: () => void;
}

// Interfaces pour les props des composants de section
interface GeneralSectionProps {
    formData: Ingredient;
    isSaving: boolean;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown; type?: string; checked?: boolean }>) => void;
}

interface PricingSectionProps {
    formData: Ingredient;
    isSaving: boolean;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown; type?: string; checked?: boolean }>) => void;
}

interface AllergenSectionProps {
    allergens: string[];
    isSaving: boolean;
    handleAllergenChange: (newAllergens: string[]) => void;
}

// Définition des composants de section EN DEHORS du composant principal
const GeneralSection = ({ formData, isSaving, handleChange }: GeneralSectionProps) => (
    <Box>
        <SectionTitle>Informations générales</SectionTitle>
        <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
            disabled={isSaving}
            variant="outlined"
        />

        <FormControl fullWidth margin="normal" disabled={isSaving} variant="outlined">
            <InputLabel>Type d'ingrédient</InputLabel>
            <Select
                name="type"
                value={formData.type}
                onChange={handleChange as any}
                label="Type d'ingrédient"
            >
                <MenuItem value={INGREDIENT_TYPES.STANDARD}>
                    Ingrédient standard
                </MenuItem>
                <MenuItem value={INGREDIENT_TYPES.BASE}>
                    Ingrédient de base (interchangeable)
                </MenuItem>
            </Select>
            <FormHelperText>
                {formData.type === INGREDIENT_TYPES.BASE ? 
                    "Peut être substitué par d'autres ingrédients de base" : 
                    "Peut être retiré du produit"}
            </FormHelperText>
        </FormControl>

        <FormControlLabel
            control={
                <Checkbox
                    checked={formData.isOutOfStock}
                    onChange={handleChange}
                    name="isOutOfStock"
                    disabled={isSaving}
                />
            }
            label="Indisponible"
            sx={{ mt: 2 }}
        />
    </Box>
);

const PricingSection = ({ formData, isSaving, handleChange }: PricingSectionProps) => (
    <Box sx={{ mt: 3 }}>
        <SectionTitle>Tarification</SectionTitle>
        
        {/* Base ingredient pricing */}
        <Collapse in={formData.type === INGREDIENT_TYPES.BASE}>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
                    Prix pour ingrédient de base
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <NumericField
                        fullWidth
                        label="Prix changement de base"
                        name="baseChangePrice"
                        type="number"
                        value={formData.baseChangePrice === null ? '' : formData.baseChangePrice}
                        onChange={handleChange}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>,
                            inputProps: { min: 0, step: 0.1 }
                        }}
                        disabled={isSaving}
                        variant="outlined"
                        helperText="Supplément facturé quand un client change d'ingrédient de base"
                    />
                    <NumericField
                        fullWidth
                        label="Prix double base"
                        name="baseDoublePrice"
                        type="number"
                        value={formData.baseDoublePrice === null ? '' : formData.baseDoublePrice}
                        onChange={handleChange}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>,
                            inputProps: { min: 0, step: 0.1 }
                        }}
                        disabled={isSaving}
                        variant="outlined"
                        helperText="Prix pour doubler l'ingrédient de base"
                    />
                </Box>
            </Paper>
        </Collapse>

        {/* Supplement section */}
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={formData.canBeExtra}
                        onChange={handleChange}
                        name="canBeExtra"
                        disabled={isSaving}
                    />
                }
                label="Peut être ajouté en supplément"
            />

            <Collapse in={formData.canBeExtra === true}>
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <NumericField
                            fullWidth
                            label="Prix supplément"
                            name="price"
                            type="number"
                            value={formData.price === null ? '' : formData.price}
                            onChange={handleChange}
                            required={formData.canBeExtra}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                inputProps: { min: 0, step: 0.1 }
                            }}
                            disabled={isSaving}
                            variant="outlined"
                            helperText="Prix pour ajouter l'ingrédient en supplément"
                        />
                        <NumericField   
                            fullWidth
                            label="Prix réduit"
                            name="discountPrice"
                            type="number"
                            value={formData.discountPrice === null ? '' : formData.discountPrice}
                            onChange={handleChange}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                inputProps: { min: 0, step: 0.1 }
                            }}
                            disabled={isSaving}
                            variant="outlined"
                            helperText="Laissez vide si pas de réduction"
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <NumericField
                            fullWidth
                            label="Quantité minimum"
                            name="minQuantity"
                            type="number"
                            value={formData.minQuantity === null ? 0 : formData.minQuantity}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            required={formData.canBeExtra}
                            disabled={isSaving}
                            variant="outlined"
                        />
                        <NumericField
                            fullWidth
                            label="Quantité maximum"
                            name="maxQuantity"
                            type="number"
                            value={formData.maxQuantity === null ? 1 : formData.maxQuantity}
                            onChange={handleChange}
                            inputProps={{ min: formData.minQuantity || 0 }}
                            required={formData.canBeExtra}
                            disabled={isSaving}
                            variant="outlined"
                        />
                    </Box>
                </Box>
            </Collapse>
        </Paper>
    </Box>
);
const AllergenSection = ({ allergens, isSaving, handleAllergenChange }: AllergenSectionProps) => (
    <Box sx={{ mt: 3 }}>
        <SectionTitle>Allergènes</SectionTitle>
        <AllergenSelect
            value={allergens}
            onChange={handleAllergenChange}
            disabled={isSaving}
        />
    </Box>
);

// Mémorisation des composants de section
const GeneralSectionMemo = memo(GeneralSection);
const PricingSectionMemo = memo(PricingSection);
const AllergenSectionMemo = memo(AllergenSection);

// Composant principal
function IngredientForm({ ingredient, onSave, onClose }: IngredientFormProps) {
    const [formData, setFormData] = useState<Ingredient>({
        name: '',
        type: INGREDIENT_TYPES.STANDARD,
        price: null,
        discountPrice: null,
        minQuantity: 0,
        maxQuantity: 1,
        isOutOfStock: false,
        allergens: [],
        canBeExtra: false,
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (ingredient) {
            setFormData({
                ...ingredient,
                // Ensure all properties are properly typed
                canBeExtra: Boolean(ingredient.price),
                price: ingredient.price !== undefined ? ingredient.price : null,
                discountPrice: ingredient.discountPrice !== undefined ? ingredient.discountPrice : null,
                baseChangePrice: ingredient.baseChangePrice !== undefined ? ingredient.baseChangePrice : null,
                baseDoublePrice: ingredient.baseDoublePrice !== undefined ? ingredient.baseDoublePrice : null,
                minQuantity: ingredient.minQuantity !== undefined ? ingredient.minQuantity : 0,
                maxQuantity: ingredient.maxQuantity !== undefined ? ingredient.maxQuantity : 1,
                allergens: ingredient.allergens || [],
                isOutOfStock: ingredient.isOutOfStock || false,
            });
        }
    }, [ingredient]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown; type?: string; checked?: boolean }>) => {
        const { name, value, type, checked } = e.target as HTMLInputElement;
        
        if (!name) return;
        
        setFormData(prevData => {
            const newData = {
                ...prevData,
                [name]: type === 'checkbox' ? checked : value,
            };

            // Handle price fields as numbers
            if (name === 'price' || name === 'discountPrice' || 
                name === 'baseChangePrice' || name === 'baseDoublePrice') {
                newData[name] = value === '' ? null : Number(value);
            }

            // Handle quantity fields as numbers
            if (name === 'minQuantity' || name === 'maxQuantity') {
                newData[name] = value === '' ? 0 : Number(value);
            }

            // Reset supplement data if no longer a supplement
            if (name === 'canBeExtra' && !checked) {
                newData.price = null;
                newData.discountPrice = null;
                newData.minQuantity = 0;
                newData.maxQuantity = 1;
            }

            return newData;
        });
    }, []);

    const handleAllergenChange = useCallback((newAllergens: string[]) => {
        setFormData(prev => ({
            ...prev,
            allergens: newAllergens
        }));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            // Prepare data for saving, making sure all fields are correctly typed
            const dataToSave: Ingredient = {
                ...formData,
                price: formData.canBeExtra || formData.type === INGREDIENT_TYPES.BASE 
                    ? (formData.price || 0) 
                    : null,
                discountPrice: (formData.canBeExtra || formData.type === INGREDIENT_TYPES.BASE) && formData.discountPrice 
                    ? formData.discountPrice 
                    : null,
                minQuantity: formData.canBeExtra 
                    ? (formData.minQuantity || 0) 
                    : null,
                maxQuantity: formData.canBeExtra 
                    ? (formData.maxQuantity || 1) 
                    : null,
            };
            
            await onSave(dataToSave);
            onClose();
        } catch (error) {
            console.error("Error saving ingredient: ", error);
            // Show error to user
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100%',
            position: 'relative',
            bgcolor: 'background.default',
        }}>
            <Box sx={{
                flexGrow: 1,
                overflowY: 'auto',
                p: 3,
                pb: 10
            }}>
                {/* En-tête */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight="bold" color="primary.main">
                        {ingredient?.id ? "Modifier l'ingrédient" : 'Nouvel ingrédient'}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                        <FaTimes />
                    </IconButton>
                </Box>

                {ingredient?.id && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            ID : <span style={{ userSelect: 'all', fontFamily: 'monospace' }}>{ingredient.id}</span>
                        </Typography>
                    </Box>
                )}

                {/* Sections */}
                <Box component="form" onSubmit={handleSubmit}>
                    <GeneralSectionMemo 
                        formData={formData} 
                        isSaving={isSaving} 
                        handleChange={handleChange} 
                    />
                    <PricingSectionMemo 
                        formData={formData} 
                        isSaving={isSaving} 
                        handleChange={handleChange} 
                    />
                    <AllergenSectionMemo
                        allergens={formData.allergens}
                        isSaving={isSaving}
                        handleAllergenChange={handleAllergenChange}
                    />
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'background.paper',
                boxShadow: '0px -2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                zIndex: 1
            }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isSaving}
                    startIcon={<FaTimes />}
                >
                    Annuler
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isSaving && (
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSaving}
                        startIcon={<FaSave />}
                    >
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default IngredientForm;